/**
 * Removing rows that appear in another file.
 *
 * The do-not-call list, the already-sold list, the list a buyer sent back. In
 * a spreadsheet this is a VLOOKUP against a second workbook and then a manual
 * sort-and-delete, and it stops being possible somewhere around a hundred
 * thousand rows. Here the two files are joined where they sit.
 *
 * The result is a new file and the original is left as it was, so a run
 * against the wrong list costs nothing: the file it started from is still there.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Modal, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useSheet, useSheets, useSuppressSheet } from '../api/queries';
import { formatNumber } from '../theme';
import { NewFileFields, fileStem, useNewFileDetails, useOpenNewFile } from './NewFileFields';

export function SheetSuppressModal({
  sheetId,
  fileName,
  columns,
  baseVersion,
  opened,
  onClose,
}: {
  sheetId: string | null;
  fileName: string;
  columns: string[];
  baseVersion: number;
  opened: boolean;
  onClose: () => void;
}) {
  const sheets = useSheets();
  const suppress = useSuppressSheet(sheetId);
  const [col, setCol] = useState<string | null>(null);
  const [againstId, setAgainstId] = useState<string | null>(null);
  const [againstCol, setAgainstCol] = useState<string | null>(null);
  const [keep, setKeep] = useState<'missing' | 'matching'>('missing');
  // The house number, for comparing addresses. A postcode matches a street,
  // so without these an address comparison is wrong by however many houses
  // are on the road.
  const [addressCol, setAddressCol] = useState<string | null>(null);
  const [againstAddressCol, setAgainstAddressCol] = useState<string | null>(null);

  // The other file's columns are not known until one is chosen.
  const against = useSheet(againstId);
  const openNewFile = useOpenNewFile();
  const details = useNewFileDetails(opened, `${fileStem(fileName)} - suppressed.csv`, '');
  // Describe the run once the list is known, unless someone has written their own.
  const listName = against.data?.file_name;
  const [described, setDescribed] = useState('');
  useEffect(() => {
    if (!listName || (details.description !== '' && details.description !== described)) return;
    const text = keep === 'missing'
      ? `${fileName} without the rows found in ${listName}.`
      : `Only the rows of ${fileName} that are in ${listName}.`;
    details.setDescription(text);
    setDescribed(text);
  }, [listName, keep]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (opened) {
      setCol(columns[0] ?? null);
      setAgainstId(null);
      setAgainstCol(null);
    }
  }, [opened, columns]);

  // A sensible default: the same column name, which is usually what a
  // suppression list uses.
  useEffect(() => {
    const theirs = against.data?.columns ?? [];
    if (theirs.length === 0) return;
    setAgainstCol(theirs.find((c) => c === col) ?? theirs[0] ?? null);
  }, [against.data?.columns, col]);

  const others = (sheets.data?.sheets ?? []).filter(
    (s) => s.id !== sheetId && s.status === 'ready',
  );

  return (
    <Modal opened={opened} onClose={onClose} title="Compare with another file" size="md">
      <Stack>
        <SegmentedControl
          value={keep}
          onChange={(v) => setKeep(v as 'missing' | 'matching')}
          data={[
            { value: 'missing', label: 'Remove rows found in it' },
            { value: 'matching', label: 'Keep only rows found in it' },
          ]}
        />

        <Select
          label="Match on this column"
          data={columns}
          value={col}
          onChange={setCol}
          searchable
          allowDeselect={false}
        />

        <Select
          label="Against this file"
          placeholder={others.length ? 'Choose a file' : 'No other files are ready'}
          data={others.map((s) => ({
            value: s.id,
            label: `${s.file_name} (${formatNumber(s.row_count)} rows)`,
          }))}
          value={againstId}
          onChange={setAgainstId}
          searchable
          disabled={others.length === 0}
        />

        <Select
          label="Matching its column"
          placeholder={againstId ? 'Choose a column' : 'Choose a file first'}
          data={against.data?.columns ?? []}
          value={againstCol}
          onChange={setAgainstCol}
          searchable
          disabled={!againstId}
        />

        {/* Optional, and paired: matching a postcode on one side against
            postcode-and-number on the other compares different things. */}
        <Select
          label="Also match a house number (optional)"
          description="For addresses. A postcode on its own matches a whole street."
          placeholder="Match on the first column only"
          clearable
          data={columns.filter((c) => c !== col)}
          value={addressCol}
          onChange={setAddressCol}
        />
        {addressCol && (
          <Select
            label="Its address column"
            placeholder={againstId ? 'Choose a column' : 'Choose a file first'}
            data={(against.data?.columns ?? []).filter((c) => c !== againstCol)}
            value={againstAddressCol}
            onChange={setAgainstAddressCol}
            searchable
            disabled={!againstId}
          />
        )}

        <Alert color="gray" variant="light">
          <Text size="sm">
            {keep === 'missing'
              ? 'Every row whose value appears in that file is removed. Blank cells never match, so an empty row in the list does not take out every blank row here.'
              : 'Only rows found in that file are kept. Rows with nothing to compare are dropped, because "might be on the list" is not the same as being on it.'}
          </Text>
        </Alert>

        <NewFileFields details={details} sourceName={fileName} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button
            color={keep === 'missing' ? 'red' : 'blue'}
            loading={suppress.isPending}
            disabled={!col || !againstId || !againstCol || (!!addressCol && !againstAddressCol)}
            onClick={() =>
              suppress.mutate(
                {
                  col: col!,
                  against_sheet_id: againstId!,
                  against_col: againstCol!,
                  address_col: addressCol,
                  against_address_col: addressCol ? againstAddressCol : null,
                  keep,
                  baseVersion,
                  name: details.name,
                  description: details.description,
                },
                {
                  onSuccess: (made) => {
                    onClose();
                    openNewFile(made, fileName);
                  },
                  onError: (e) =>
                    notifications.show({
                      color: 'red',
                      title: 'Could not suppress',
                      message: (e as Error).message,
                    }),
                },
              )
            }
          >
            Save as a new file
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
